import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Layout } from '@surecompanies/core_components'
import Page from 'layouts/Page'
import BreadcrumbNav from '../../../../components/Breadcrumbs'
import { HRIS_PATH } from '../../constants'
import { message } from 'antd'
import { Button } from '@sureco/design-system'
import AssistedManualCensusUploadModal from './components/AssistedManualCensusUploadModal'
import AssistedManualCensusFilesGrid from './components/AssistedManualFilesGrid'
import ClientSelect from '../../../../../components/ClientSelect'
import { SimpleCustomerDto } from '../../../../api/persons/dto/simple-customer.dto'
import '../AssistedManual.scss'
import { getMyClients } from '../../../../api/persons'
import CensusQueueGrid from '../../../CensusManagement/components/CensusQueueGrid'
import { CensusUploadJobChannel } from '../../../CensusManagement/api/dto/census-upload-job.dto'

const AMA_QUEUE_CHANNELS: CensusUploadJobChannel[] = ['AMA']

const { Content } = Layout

export default function AssistedManualCensus() {
  const [clients, setClients] = useState<SimpleCustomerDto[]>([])
  const [isModalVisible, setIsModalVisible] = useState(false) // State to control modal visibility
  const [fileUpdated, setFileUpdated] = useState<boolean>(false)
  const [queueUpdated, setQueueUpdated] = useState<boolean>(false)
  const [selectedClient, setSelectedClient] = useState<
    SimpleCustomerDto | undefined
  >()
  useEffect(() => {
    const getClients = async () => {
      const customers: SimpleCustomerDto[] = await getMyClients()
      setClients(customers)
    }
    getClients()
  }, [])

  const handleCompanyChange = (value: string) => {
    const c = clients.find((c) => c.id === value)
    setSelectedClient(c)
  }

  const clientNameLookup = useMemo(() => {
    return clients.reduce((acc, client) => {
      acc[client.id] = client.name
      return acc
    }, {})
  }, [clients]) // Rebuild lookup only if clients change

  const handleUploadSuccess = async (isQueued: boolean) => {
    try {
      setIsModalVisible(false)
      // Same wording as Census Management V2; this page is platform-admin only
      message.success(
        isQueued
          ? 'Upload queued. Follow its progress in the Queue section.'
          : 'File uploaded successfully!'
      )
      // this is just a toggle to refresh the child
      setFileUpdated(!fileUpdated)
      // A queued upload shows in the Queue section until its job finishes
      refreshQueue()
    } catch (error) {
      const msg = (error as any)?.response?.data?.message ?? error
      message.error(`Failed to upload file: ${msg}`)
    }
  }

  const refreshQueue = useCallback(
    () => setQueueUpdated((updated) => !updated),
    []
  )
  const queueFilter = useMemo(() => ({ selectedClient }), [selectedClient])
  // A finished job adds a report or marks a file submitted in the Census grid
  const refreshFiles = useCallback(
    () => setFileUpdated((updated) => !updated),
    []
  )

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  return (
    <Page
      title="Assisted Manual - Census"
      action={
        <>
          <Button onClick={() => setIsModalVisible(true)}>
            Upload HRIS report
          </Button>
          <ClientSelect
            allCompaniesPlacholder={{ id: '', companyName: 'All Companies' }}
            companies={clients.map(({ name, id }) => ({
              companyName: name,
              id: id,
            }))}
            defaultCompanyId={''}
            selectedCompanyId={selectedClient?.id}
            onChange={handleCompanyChange}
          />
        </>
      }
      breadcrumb={
        <BreadcrumbNav
          items={[
            { title: 'HRIS', path: HRIS_PATH },
            { title: 'Assisted Manual - Census' },
          ]}
        />
      }
    >
      <AssistedManualCensusUploadModal
        open={isModalVisible}
        onOk={handleUploadSuccess}
        onCancel={handleCancel}
      />
      <Layout style={{ backgroundColor: '#f6f9fc' }}>
        <Content style={{}}>
          <CensusQueueGrid
            clientNameLookup={clientNameLookup}
            refreshKey={queueUpdated}
            filter={queueFilter}
            channel={AMA_QUEUE_CHANNELS}
            onJobsFinished={refreshFiles}
            showAction
          />
          <AssistedManualCensusFilesGrid
            selectedClientId={selectedClient?.id}
            fileUpdated={fileUpdated}
            clientNameLookup={clientNameLookup}
            onFilesChanged={refreshQueue}
          />
        </Content>
      </Layout>
    </Page>
  )
}
